import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  checkMarkIcon as CheckMarkIcon,
  eyeClosed as EyeClosedIcon,
  eyeIcon as EyeIcon,
} from "../../assets/icons";
import { useNotify } from "../../context/NotificationContext";
import {
  attachExistingStudentAccount,
  fetchStudentLinkDetail,
} from "../../services/studentPortalService/studentPortalService";
import type { StudentLinkDetail } from "../../services/studentPortalService/studentPortalService.types";
import { getServerErrorMessage, getServerFieldErrors } from "../../utils/httpError";
import { logger } from "../../utils/logger";
import {
  LinkField,
  LinkFieldError,
  LinkFootnote,
  LinkForm,
  LinkInput,
  LinkInputWrap,
  LinkLabel,
  LinkLead,
  LinkPageContent,
  LinkPageShell,
  LinkPageTitle,
  LinkPrimaryAction,
  LinkSectionTitle,
  LinkStateAction,
  LinkStateMessage,
  PasswordInput,
  PasswordToggle,
  SuccessIconCircle,
  SuccessState,
  SuccessText,
} from "./StudentLinkPage.styles";

const ATTACH_FIELDS = ["email", "password"] as const;
const STUDENT_LOGIN_PATH = "/login?role=student";

const openStudentCabinetInBrowser = () => {
  const url = new URL(STUDENT_LOGIN_PATH, window.location.origin).toString();
  const openLink = window.Telegram?.WebApp?.openLink;

  if (typeof openLink === "function") {
    openLink.call(window.Telegram.WebApp, url);
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
};

const StudentLinkPage: React.FC = () => {
  const { token = "" } = useParams();
  const [detail, setDetail] = useState<StudentLinkDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [attached, setAttached] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [fatalError, setFatalError] = useState("");
  const navigate = useNavigate();
  const toast = useNotify();

  useEffect(() => {
    let active = true;
    fetchStudentLinkDetail(token)
      .then((data) => active && setDetail(data))
      .catch((error) => {
        logger.error("Ошибка открытия student link:", error);
        const message = getServerErrorMessage(
          error,
          "Ссылка недоступна или срок ее действия истек.",
        );
        if (active) {
          setFatalError(message);
          toast.error("Не удалось открыть ссылку", message);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, toast]);

  const canSubmit = useMemo(
    () => Boolean(formData.email.trim() && formData.password),
    [formData.email, formData.password],
  );

  const updateField = (field: "email" | "password", value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
    if (externalErrors[field]) {
      setExternalErrors((current) => ({ ...current, [field]: "" }));
    }
  };

  const attach = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = formData.email.trim();
    const validationErrors: Record<string, string> = {};
    if (!email || !email.includes("@")) {
      validationErrors.email = "Введите корректную электронную почту";
    }
    if (!formData.password) {
      validationErrors.password = "Введите пароль";
    }
    if (Object.keys(validationErrors).length > 0) {
      setExternalErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setExternalErrors({});
    try {
      await attachExistingStudentAccount(token, email, formData.password);
      setAttached(true);
    } catch (error) {
      logger.error("Ошибка привязки student account:", error);
      const fieldErrors = getServerFieldErrors(error, ATTACH_FIELDS);
      setExternalErrors(fieldErrors);
      toast.error(
        "Не удалось привязать аккаунт",
        getServerErrorMessage(error, "Не удалось привязать аккаунт"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = <LinkPageTitle>Веб-кабинет ученика</LinkPageTitle>;

  if (loading) {
    return (
      <LinkPageShell>
        <LinkPageContent>
          {pageTitle}
          <LinkStateMessage>Проверяем ссылку…</LinkStateMessage>
        </LinkPageContent>
      </LinkPageShell>
    );
  }

  if (fatalError || !detail) {
    return (
      <LinkPageShell>
        <LinkPageContent>
          {pageTitle}
          <LinkStateMessage $danger>{fatalError || "Ссылка недоступна."}</LinkStateMessage>
        </LinkPageContent>
      </LinkPageShell>
    );
  }

  return (
    <LinkPageShell>
      <LinkPageContent>
        {pageTitle}

        {attached ? (
          <SuccessState>
            <SuccessIconCircle aria-hidden="true">
              <CheckMarkIcon />
            </SuccessIconCircle>
            <LinkSectionTitle>Аккаунт привязан</LinkSectionTitle>
            <SuccessText>
              Telegram и веб-кабинет теперь используют один профиль ученика и общий прогресс.
            </SuccessText>
            <LinkPrimaryAction
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={openStudentCabinetInBrowser}
            >
              Перейти в кабинет
            </LinkPrimaryAction>
          </SuccessState>
        ) : detail.has_web_auth ? (
          <>
            <LinkSectionTitle>Web-аккаунт уже подключен</LinkSectionTitle>
            <LinkLead>Войдите в кабинет ученика под своей почтой и паролем.</LinkLead>
            <LinkStateAction
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={openStudentCabinetInBrowser}
            >
              Войти
            </LinkStateAction>
          </>
        ) : detail.purpose === "signup_from_telegram" ? (
          <>
            <LinkSectionTitle>Создать web-аккаунт</LinkSectionTitle>
            <LinkLead>
              Telegram уже будет связан с новым аккаунтом. Курсы и учебные данные останутся общими.
            </LinkLead>
            <LinkStateAction
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={() =>
                navigate(`/signup?role=student&link_token=${encodeURIComponent(token)}`)
              }
            >
              Продолжить регистрацию
            </LinkStateAction>
          </>
        ) : (
          <>
            <LinkSectionTitle>Привязать аккаунт</LinkSectionTitle>
            <LinkLead>
              Введите данные существующего кабинета. После привязки курсы и прогресс будут доступны и в Telegram, и в веб-кабинете.
            </LinkLead>

            <LinkForm onSubmit={attach} noValidate>
              <LinkField>
                <LinkLabel htmlFor="student-link-email">Электронная почта</LinkLabel>
                <LinkInputWrap>
                  <LinkInput
                    id="student-link-email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    $hasError={Boolean(externalErrors.email)}
                    aria-invalid={Boolean(externalErrors.email)}
                    aria-describedby="student-link-email-error"
                    onChange={(event) => updateField("email", event.target.value)}
                  />
                </LinkInputWrap>
                <LinkFieldError id="student-link-email-error" aria-live="polite">
                  {externalErrors.email || ""}
                </LinkFieldError>
              </LinkField>

              <LinkField>
                <LinkLabel htmlFor="student-link-password">Пароль</LinkLabel>
                <LinkInputWrap>
                  <PasswordInput
                    id="student-link-password"
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Введите пароль"
                    value={formData.password}
                    $hasError={Boolean(externalErrors.password)}
                    aria-invalid={Boolean(externalErrors.password)}
                    aria-describedby="student-link-password-error"
                    onChange={(event) => updateField("password", event.target.value)}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setPasswordVisible((current) => !current)}
                    aria-label={passwordVisible ? "Скрыть пароль" : "Показать пароль"}
                  >
                    {passwordVisible ? <EyeClosedIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
                  </PasswordToggle>
                </LinkInputWrap>
                <LinkFieldError id="student-link-password-error" aria-live="polite">
                  {externalErrors.password || ""}
                </LinkFieldError>
              </LinkField>

              <LinkPrimaryAction
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
                disabled={!canSubmit || submitting}
              >
                {submitting ? "Привязываем…" : "Привязать аккаунт"}
              </LinkPrimaryAction>
            </LinkForm>

            <LinkFootnote>
              Привязка не создаёт новый аккаунт и не меняет ваши учебные данные.
            </LinkFootnote>
          </>
        )}
      </LinkPageContent>
    </LinkPageShell>
  );
};

export default StudentLinkPage;
